import React, { useState } from "react";
import { useAirQualityHotspots, useWeeklyAirAverage } from "../hooks/useData";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Cell } from "recharts";

const PARAMETERS = ["pm25", "pm10", "o3", "no2", "so2", "co"];
const PARAM_LABELS = { pm25: "PM2.5", pm10: "PM10", o3: "Ozone", no2: "NO₂", so2: "SO₂", co: "CO" };

const AQI_COLORS = {
  good: "#10B981", moderate: "#FBBF24",
  unhealthy_sensitive: "#F97316", unhealthy: "#F97316",
  very_unhealthy: "#EF4444", hazardous: "#FF4444", unknown: "#7A8EA8",
};

export default function AirQuality() {
  const [param, setParam] = useState("pm25");
  const { data: hotspots, isLoading } = useAirQualityHotspots(param, 15);
  const { data: weeklyAvg } = useWeeklyAirAverage(param);

  const list = hotspots?.hotspots || [];
  const avg = weeklyAvg?.weekly_average;
  const category = weeklyAvg?.aqi_category || "unknown";

  return (
    <div className="fade-in">
      <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 6 }}>AIR QUALITY</h1>
      <p style={{ color: "var(--subtext)", fontSize: 12, marginBottom: 20 }}>
        Real-time global pollution monitoring via OpenAQ
      </p>

      {/* Parameter selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {PARAMETERS.map(p => (
          <button
            key={p}
            onClick={() => setParam(p)}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${p === param ? "var(--accent)" : "var(--border)"}`,
              background: p === param ? "rgba(0,229,176,0.1)" : "var(--surface)",
              color: p === param ? "var(--accent)" : "var(--subtext)",
              fontFamily: "var(--mono)",
              fontSize: 11,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {PARAM_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Weekly avg stat */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ flex: "0 0 auto", minWidth: 200 }}>
          <div className="card-title">7-Day Global Average</div>
          <div style={{
            fontSize: 36, fontFamily: "var(--mono)", fontWeight: 700,
            color: AQI_COLORS[category],
          }}>
            {avg != null ? `${avg}` : "—"}
            <span style={{ fontSize: 14, color: "var(--subtext)", marginLeft: 6 }}>µg/m³</span>
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={`badge badge-${category.replace("_sensitive", "").replace("_unhealthy", "").replace("very_", "")}`}>
              {category.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div className="card-title">What is {PARAM_LABELS[param]}?</div>
          <div style={{ fontSize: 12, color: "var(--subtext)", lineHeight: 1.7 }}>
            {param === "pm25" && "Fine particulate matter ≤2.5 micrometers. Primary indicator of air quality and respiratory health risk. Sources: combustion, industry, vehicles."}
            {param === "pm10" && "Coarse particulate matter ≤10 micrometers. Dust, pollen, mold. Elevated near industrial and construction zones."}
            {param === "o3" && "Ground-level ozone. Formed from vehicle and industrial emissions reacting in sunlight. Strong lung irritant."}
            {param === "no2" && "Nitrogen dioxide from vehicle combustion and power plants. Indicator of traffic-related pollution in urban areas."}
            {param === "so2" && "Sulfur dioxide from coal burning and volcanic activity. Causes acid rain and respiratory damage."}
            {param === "co" && "Carbon monoxide from incomplete combustion. Reduces oxygen carrying capacity of blood."}
          </div>
        </div>
      </div>

      {/* Hotspot chart */}
      <div className="card">
        <div className="card-title">Top Pollution Hotspots · {PARAM_LABELS[param]}</div>
        {isLoading ? (
          Array(8).fill(0).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 28, marginBottom: 8 }} />
          ))
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={list} layout="vertical" barSize={14} margin={{ left: 10 }}>
              <XAxis type="number" tick={{ fill: "var(--subtext)", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="city"
                width={100}
                tick={{ fill: "var(--subtext)", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v?.slice(0, 16) || "Unknown"}
              />
              <Tooltip
                contentStyle={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
                formatter={(v, _, props) => [
                  `${v} µg/m³ · ${props.payload?.aqi_category?.replace(/_/g, " ")}`,
                  PARAM_LABELS[param]
                ]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {list.map((h, i) => (
                  <Cell key={i} fill={AQI_COLORS[h.aqi_category] || AQI_COLORS.unknown} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
